/**
 * gateStack — tiny global counter for "is a grown-up gate up right now?"
 *
 * Used to suppress social/friend visibility while any of the safety
 * gates are open:
 *  - ParentalGate (math challenge for mic / link / purchase)
 *  - ConsentGateModal (kid-side VPC gate)
 *  - ReacceptTermsGate (blocking Terms reaccept)
 *
 * Each gate calls ``useGatePresence(open)`` which increments the count
 * while ``open`` is true and decrements on close/unmount. Consumers
 * read via ``useActiveGateCount()`` or check ``isAnyGateActive()``.
 *
 * The "social hides when a gate is up" rule is implemented in
 * useSocial.ts which subscribes here.
 */

import { useEffect } from "react";

let _count = 0;
const _listeners = new Set<(n: number) => void>();

function _broadcast(): void {
  for (const fn of _listeners) fn(_count);
}

export function isAnyGateActive(): boolean {
  return _count > 0;
}

export function subscribe(fn: (n: number) => void): () => void {
  _listeners.add(fn);
  return () => {
    _listeners.delete(fn);
  };
}

/**
 * Hook for a gate component to register its presence. While ``open`` is
 * true, the global counter is incremented; flips off on unmount or when
 * ``open`` becomes false.
 */
export function useGatePresence(open: boolean): void {
  useEffect(() => {
    if (!open) return;
    _count += 1;
    _broadcast();
    return () => {
      _count = Math.max(0, _count - 1);
      _broadcast();
    };
  }, [open]);
}
